export type Coarse = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'any';

// ---------- TypeExpr (parametric, minimal) ----------
export type TypeExpr =
  | { tag: 'any' }
  | { tag: 'coarse'; name: Exclude<Coarse, 'array' | 'any'> }
  | { tag: 'array'; elem: TypeExpr }
  | { tag: 'tuple'; elems: TypeExpr[]; rest?: TypeExpr }
  | { tag: 'record'; key: TypeExpr; value: TypeExpr }
  | { tag: 'shape'; props: Record<string, TypeExpr>; additional?: TypeExpr }
  | { tag: 'var'; name: string };

export const ANY_T: TypeExpr = { tag: 'any' };
export const T_STRING: TypeExpr = { tag: 'coarse', name: 'string' };
export const T_NUMBER: TypeExpr = { tag: 'coarse', name: 'number' };
export const T_BOOLEAN: TypeExpr = { tag: 'coarse', name: 'boolean' };
export const T_OBJECT: TypeExpr = { tag: 'coarse', name: 'object' };
export const arrayOf = (elem: TypeExpr): TypeExpr => ({ tag: 'array', elem });
export const recordOf = (key: TypeExpr, value: TypeExpr): TypeExpr => ({ tag: 'record', key, value });
export const tupleOf = (...elems: TypeExpr[]): TypeExpr => ({ tag: 'tuple', elems });

export function exprToCoarse(t: TypeExpr): Coarse {
  switch (t.tag) {
    case 'any':
      return 'any';
    case 'coarse':
      return t.name;
    case 'array':
      return 'array';
    case 'tuple':
      return 'array';
    case 'record':
      return 'object';
    case 'shape':
      return 'object';
    case 'var':
      return 'any';
  }
}

export function typeExprEquals(a: TypeExpr, b: TypeExpr): boolean {
  if (a.tag !== b.tag) return false;
  switch (a.tag) {
    case 'any':
      return true;
    case 'var':
      return (b as any).name === a.name;
    case 'coarse':
      return (b as any).name === a.name;
    case 'tuple': {
      const bb = b as any as { elems: TypeExpr[]; rest?: TypeExpr };
      if (a.elems.length !== bb.elems.length) return false;
      for (let i = 0; i < a.elems.length; i++) {
        if (!typeExprEquals(a.elems[i], bb.elems[i])) return false;
      }
      if (!!a.rest !== !!bb.rest) return false;
      return a.rest ? typeExprEquals(a.rest, bb.rest!) : true;
    }
    case 'array':
      return typeExprEquals(a.elem, (b as any).elem);
    case 'record':
      return (
        (b as any).key &&
        (b as any).value &&
        typeExprEquals((a as any).key, (b as any).key) &&
        typeExprEquals((a as any).value, (b as any).value)
      );
    case 'shape': {
      const bb = b as any as { props: Record<string, TypeExpr>; additional?: TypeExpr };
      const ak = Object.keys(a.props).sort();
      const bk = Object.keys(bb.props).sort();
      if (ak.length !== bk.length) return false;
      for (const k of ak) {
        if (!(k in bb.props)) return false;
        if (!typeExprEquals(a.props[k], bb.props[k])) return false;
      }
      const aadd = (a as any).additional;
      const badd = (b as any).additional;
      if (!!aadd !== !!badd) return false;
      return aadd ? typeExprEquals(aadd, badd!) : true;
    }
  }
}

export function isTypeCompatibleExpr(a: TypeExpr, b: TypeExpr): boolean {
  if (a.tag === 'any' || b.tag === 'any') return true;
  // shape/object cross-compatibility
  if (a.tag === 'shape' && b.tag === 'coarse' && b.name === 'object') return true;
  if (b.tag === 'shape' && a.tag === 'coarse' && a.name === 'object') return true;
  if (a.tag === 'shape' && b.tag === 'record') {
    // every prop value compatible with record.value
    if (!isTypeCompatibleExpr(T_STRING, (b as any).key)) return false;
    for (const v of Object.values((a as any).props)) {
      if (!isTypeCompatibleExpr(v, (b as any).value)) return false;
    }
    return true;
  }
  if (b.tag === 'shape' && a.tag === 'record') {
    if (!isTypeCompatibleExpr(T_STRING, (a as any).key)) return false;
    for (const v of Object.values((b as any).props)) {
      if (!isTypeCompatibleExpr((a as any).value, v)) return false;
    }
    return true;
  }
  if (a.tag === 'shape' && b.tag === 'shape') {
    // a is assignable to b if a has at least the keys of b and each value is compatible
    for (const [k, bv] of Object.entries((b as any).props)) {
      const av = (a as any).props[k];
      if (!av || !isTypeCompatibleExpr(av, bv)) return false;
    }
    return true;
  }
  if (a.tag === 'tuple' && b.tag === 'tuple') {
    if (a.elems.length !== b.elems.length) return false;
    for (let i = 0; i < a.elems.length; i++) {
      if (!isTypeCompatibleExpr(a.elems[i], b.elems[i])) return false;
    }
    if (a.rest || b.rest) return !!(a.rest && b.rest && isTypeCompatibleExpr(a.rest, b.rest));
    return true;
  }
  if (a.tag === 'array' && b.tag === 'array') {
    return isTypeCompatibleExpr(a.elem, b.elem);
  }
  if (a.tag === 'record' && b.tag === 'record') {
    // Keys and values must be compatible
    return isTypeCompatibleExpr(a.key, b.key) && isTypeCompatibleExpr(a.value, b.value);
  }
  return a.tag === 'coarse' && b.tag === 'coarse' && a.name === b.name;
}

export function unifyTypeExpr(a: TypeExpr, b: TypeExpr): TypeExpr {
  if (typeExprEquals(a, b)) return a;
  if (a.tag === 'any') return b;
  if (b.tag === 'any') return a;
  if (a.tag === 'shape' && b.tag === 'coarse' && b.name === 'object') return a;
  if (b.tag === 'shape' && a.tag === 'coarse' && a.name === 'object') return b;
  if (a.tag === 'shape' && b.tag === 'record') {
    // Prefer keeping the concrete shape so node-local projections (e.g., shapeProp) can see structure.
    // Unify each shape property with the record's value type to respect downstream constraints.
    const bv = (b as any).value as TypeExpr;
    const props: Record<string, TypeExpr> = {};
    for (const [k, av] of Object.entries((a as any).props)) {
      props[k] = unifyTypeExpr(av as TypeExpr, bv);
    }
    return { tag: 'shape', props };
  }
  if (b.tag === 'shape' && a.tag === 'record') {
    const av = (a as any).value as TypeExpr;
    const props: Record<string, TypeExpr> = {};
    for (const [k, bv] of Object.entries((b as any).props)) {
      props[k] = unifyTypeExpr(av, bv as TypeExpr);
    }
    return { tag: 'shape', props };
  }
  if (a.tag === 'shape' && b.tag === 'shape') {
    const keys = Array.from(new Set([...Object.keys((a as any).props), ...Object.keys((b as any).props)]));
    const props: Record<string, TypeExpr> = {};
    for (const k of keys) {
      const av = (a as any).props[k];
      const bv = (b as any).props[k];
      props[k] = av && bv ? unifyTypeExpr(av, bv) : (av ?? bv)!;
    }
    return { tag: 'shape', props };
  }
  if (a.tag === 'tuple' && b.tag === 'tuple') {
    if (a.elems.length !== b.elems.length) return ANY_T;
    const elems = a.elems.map((t, i) => unifyTypeExpr(t, b.elems[i]));
    const rest = a.rest && b.rest ? unifyTypeExpr(a.rest, b.rest) : undefined;
    return { tag: 'tuple', elems, rest };
  }
  if (a.tag === 'array' && b.tag === 'array') {
    const elem = unifyTypeExpr(a.elem, b.elem);
    return { tag: 'array', elem };
  }
  if (a.tag === 'record' && b.tag === 'record') {
    const key = unifyTypeExpr(a.key, b.key);
    const value = unifyTypeExpr(a.value, b.value);
    return { tag: 'record', key, value };
  }
  // incompatible coarse -> lose specificity
  return ANY_T;
}

export function schemaToCoarse(schema: any): Coarse {
  try {
    const t = typeof schema === 'object' && schema ? schema.type ?? (Array.isArray(schema.enum) ? 'string' : undefined) : undefined;
    if (t === 'integer') return 'number';
    if (t === 'number' || t === 'string' || t === 'boolean' || t === 'object' || t === 'array') return t;
  } catch {}
  return 'any';
}

export function isTypeCompatible(a: Coarse, b: Coarse): boolean {
  if (a === 'any' || b === 'any') return true;
  if (a === b) return true;
  if (a === 'number' && b === 'number') return true;
  return false;
}

// New: parse JSON Schema → TypeExpr
export function schemaToTypeExpr(schema: any): TypeExpr {
  try {
    if (!schema || typeof schema !== 'object') return ANY_T;
    const t = (schema as any).type as string | string[] | undefined;
    if (Array.isArray(t)) {
      // union types not yet supported -> degrade
      return ANY_T;
    }
    if (t === 'array') {
      // Tuples (draft-2020-12): prefixItems + optional items (rest)
      const px = (schema as any).prefixItems;
      if (Array.isArray(px)) {
        const elems = px.map(schemaToTypeExpr);
        const restSchema = (schema as any).items;
        const rest = restSchema && typeof restSchema === 'object' && !Array.isArray(restSchema)
          ? schemaToTypeExpr(restSchema)
          : undefined;
        return arrayOf({ tag: 'tuple', elems, rest });
      }
      // Tuples (older drafts): items as array + optional additionalItems (rest)
      if (Array.isArray((schema as any).items)) {
        const elems = ((schema as any).items as any[]).map(schemaToTypeExpr);
        const addl = (schema as any).additionalItems;
        const rest = addl && typeof addl === 'object' ? schemaToTypeExpr(addl) : undefined;
        return arrayOf({ tag: 'tuple', elems, rest });
      }
      // Regular homogeneous array
      const items = (schema as any).items;
      if (!items || (typeof items === 'object' && Object.keys(items).length === 0)) {
        return arrayOf(ANY_T);
      }
      return arrayOf(schemaToTypeExpr(items));
    }
    if (t === 'integer') return T_NUMBER;
    if (t === 'number') return T_NUMBER;
    if (t === 'string') return T_STRING;
    if (t === 'boolean') return T_BOOLEAN;
    if (t === 'object') {
      const props = (schema as any).properties || undefined;
      const ap = (schema as any).additionalProperties;
      if (props && typeof props === 'object' && ap !== true) {
        const shape: Record<string, TypeExpr> = {};
        for (const [k, v] of Object.entries(props)) {
          shape[k] = schemaToTypeExpr(v);
        }
        const additional = ap && typeof ap === 'object' ? schemaToTypeExpr(ap) : undefined;
        return { tag: 'shape', props: shape, additional };
      }
      if (ap && typeof ap === 'object') {
        const v = schemaToTypeExpr(ap);
        return recordOf(T_STRING, v);
      }
      return T_OBJECT;
    }
    // heuristic: properties implies object
    if ((schema as any).properties || (schema as any).additionalProperties != null) return T_OBJECT;
  } catch {}
  return ANY_T;
}

// --- Literal → TypeExpr -----------------------------------------------------
export function valueToTypeExpr(
  value: unknown,
  opts?: { depth?: number; maxProps?: number; maxTuple?: number }
): TypeExpr {
  const DEPTH = Math.max(0, opts?.depth ?? 5);
  const MAX_PROPS = Math.max(1, opts?.maxProps ?? 100);
  const MAX_TUPLE = Math.max(1, opts?.maxTuple ?? 64);
  if (DEPTH === 0) return ANY_T;
  if (value == null) return ANY_T;
  const t = typeof value;
  if (t === 'string') return T_STRING;
  if (t === 'number') return T_NUMBER;
  if (t === 'boolean') return T_BOOLEAN;
  if (Array.isArray(value)) {
    const elems = (value as any[])
      .slice(0, MAX_TUPLE)
      .map((v) => valueToTypeExpr(v, { ...opts, depth: DEPTH - 1 }));
    if (elems.length > 0) {
      // Try to homogenize into a single element type; if that works, prefer arrayOf(elem)
      let unified = elems[0]!;
      for (let i = 1; i < elems.length; i++) {
        unified = unifyTypeExpr(unified, elems[i]!);
        if (unified.tag === 'any') break;
      }
      if (unified.tag !== 'any') return arrayOf(unified);
    }
    // Fallback: retain tuple precision for heterogeneous arrays
    return arrayOf({ tag: 'tuple', elems });
  }
  if (t === 'object') {
    try {
      const obj = value as Record<string, unknown>;
      const entries = Object.entries(obj).slice(0, MAX_PROPS);
      const props: Record<string, TypeExpr> = {};
      for (const [k, v] of entries) {
        props[k] = valueToTypeExpr(v, { ...opts, depth: DEPTH - 1 });
      }
      return { tag: 'shape', props };
    } catch {}
  }
  return ANY_T;
}

// --- Bindings / Precedence ---------------------------------------------------
export type BindingPrecedence = 'pin>attr' | 'attr>pin' | 'pinOnly' | 'attrOnly';
export type CombineMode = 'replace' | 'merge';

export type BindingSpec = {
  /** Raw pin handle id to read from (data:in:<pin>) */
  pin?: string;
  /** Attribute path (usually the same as the field key) */
  attr?: string;
  precedence?: BindingPrecedence;
  combine?: CombineMode;
  sensitive?: boolean;
  exposable?: boolean; // false => do not auto-create a pin for this binding
};

export type NodeBindings = Record<string, BindingSpec>;
